import * as React from 'react';
import { Nav, Overlay, Popover, Badge, Button } from 'react-bootstrap';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './settings-announcement.css';

// Bump this id whenever a NEW announcement should re-surface for everyone who
// has already dismissed the previous one. The "New" badge reappears for any
// user whose localStorage doesn't yet have this id marked as seen.
const ANNOUNCEMENT_ID = 'whats-new-2026-07';

const ANNOUNCEMENT_TITLE = "What's New";
const ANNOUNCEMENT_INTRO = "We've been busy — here's what's new in LabSmarts:";

// Running changelog, newest release first. Each release groups its features and
// setting groups under a release date. To announce a new batch, add a new entry
// at the TOP (keep the older ones below) and bump ANNOUNCEMENT_ID so the "New"
// badge re-surfaces. `features` are plain titles; `settings` names match the
// headers on the Settings page and the description explains what each controls.
const RELEASES: Array<{
  date: string;
  features: string[];
  settings: Array<{ name: string; desc: string }>;
  // Optional "Read more" write-up for this specific release.
  blogUrl?: string;
}> = [
  {
    date: 'July 2026',
    features: [
      'Results Overview redesign',
      'About tab in every section for marker info',
      'Notes tab redesign with a consolidated Section Notes summary',
      'Estimated whole-blood viscosity calculation',
      'Raw data export with an AI prompt',
    ],
    settings: [
      {
        name: 'Test Comparison Bar Graphs',
        desc: 'also chart markers measured in the previous test but not the current one',
      },
    ],
  },
  {
    date: 'June 2026',
    features: [
      'Results Overview',
      'Markers Outside Optimal',
      'Glucose Dysregulation gauge & patterns',
      'Stress & Adrenal Balance section',
      'Floating Save button when adding & editing tests',
      'BMI bar graph',
    ],
    settings: [
      {
        name: 'PDF Inclusion',
        desc: 'choose which sections appear in the exported PDF',
      },
      {
        name: 'Markers Outside Optimal',
        desc: 'show, collapse, or hide the reference-range bar charts',
      },
      {
        name: 'Inflammation Section',
        desc: 'show only markers that signal here, or all of them',
      },
      {
        name: 'Immune Section',
        desc: 'control which immune markers and differential graphs appear',
      },
      {
        name: 'Blood Work Comparison Section Order',
        desc: 'match the analysis page order or the classic lab-report order',
      },
    ],
    blogUrl: 'https://labsmarts.com/new-labsmarts-features-june-2026/',
  },
];

interface OwnProps {
  // Namespaces the "seen" flag per practitioner so each account dismisses the
  // note independently (matching the existing per-profile localStorage pattern).
  userProfileId?: string;
}

type Props = OwnProps & RouteComponentProps;

type State = {
  showNote: boolean;
  dismissed: boolean;
};

class SettingsAnnouncementBase extends React.Component<Props, State> {
  // Anchor for the controlled Overlay so the popover positions under the badge.
  private targetRef = React.createRef<HTMLDivElement>();

  state: State = {
    showNote: false,
    dismissed: this.readDismissed(),
  };

  private storageKey(): string {
    return `${this.props.userProfileId}-announcement-${ANNOUNCEMENT_ID}`;
  }

  private readDismissed(): boolean {
    // Until the profile loads we don't know the user, so suppress the badge to
    // avoid flashing it for someone who already dismissed it. componentDidUpdate
    // re-reads the real value once userProfileId arrives.
    if (!this.props.userProfileId) {
      return true;
    }
    return localStorage.getItem(this.storageKey()) === 'true';
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.userProfileId !== this.props.userProfileId) {
      this.setState({ dismissed: this.readDismissed() });
    }
  }

  // Open/close the note without dismissing it — lets a user re-read it later.
  toggleNote = () => {
    this.setState(prev => ({ showNote: !prev.showNote }));
  };

  closeNote = () => {
    this.setState({ showNote: false });
  };

  // Permanently clears the "New" badge for this user (persists across reloads).
  dismiss = () => {
    if (this.props.userProfileId) {
      localStorage.setItem(this.storageKey(), 'true');
    }
    this.setState({ dismissed: true, showNote: false });
  };

  goToSettings = () => {
    this.dismiss();
    this.props.history.push('/settings');
  };

  render() {
    // Wait for the profile so the dismissed state is keyed to the right user.
    if (!this.props.userProfileId) {
      return null;
    }

    const { showNote, dismissed } = this.state;

    return (
      <Nav.Item>
        <div
          ref={this.targetRef}
          className="nav-link"
          role="button"
          onClick={this.toggleNote}
          style={{
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            color: '#fff',
          }}
        >
          <FontAwesomeIcon icon="bell" className="ml-2 mr-2" />
          <span>What&apos;s New</span>
          {!dismissed && (
            <Badge variant="danger" pill className="ml-1">
              New
            </Badge>
          )}
        </div>

        <Overlay
          target={this.targetRef.current}
          show={showNote}
          placement="bottom"
          rootClose
          onHide={this.closeNote}
        >
          <Popover
            id="settings-announcement-popover"
            style={{ maxWidth: '370px' }}
          >
            <Popover.Title as="h3">{ANNOUNCEMENT_TITLE}</Popover.Title>
            <Popover.Content style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <p className="mb-2">{ANNOUNCEMENT_INTRO}</p>

              {RELEASES.map(release => (
                <div key={release.date} className="whats-new-release mb-3">
                  <div className="whats-new-release-date">
                    Release date: {release.date}
                  </div>

                  {release.features.length > 0 && (
                    <>
                      <div className="font-weight-bold mb-1">New features</div>
                      <ul className="whats-new-list mb-2">
                        {release.features.map(feature => (
                          <li key={feature}>{feature}</li>
                        ))}
                      </ul>
                    </>
                  )}

                  {release.settings.length > 0 && (
                    <>
                      <div className="font-weight-bold mb-1">New settings</div>
                      <ul className="whats-new-list mb-2">
                        {release.settings.map(setting => (
                          <li key={setting.name}>
                            <strong>{setting.name}</strong> &mdash; {setting.desc}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {release.blogUrl && (
                    <p className="whats-new-release-blog mb-0">
                      <a
                        href={release.blogUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Read more about the {release.date} release &rarr;
                      </a>
                    </p>
                  )}
                </div>
              ))}

              <div className="d-flex justify-content-between align-items-center">
                <Button variant="primary" size="sm" onClick={this.goToSettings}>
                  View Settings
                </Button>
                <Button variant="link" size="sm" onClick={this.dismiss}>
                  Got it
                </Button>
              </div>
            </Popover.Content>
          </Popover>
        </Overlay>
      </Nav.Item>
    );
  }
}

export const SettingsAnnouncement = withRouter(SettingsAnnouncementBase);
